import { useEffect, useState } from "react";
import { loadSfxPrefs, saveSfxPrefs, unlockAudio } from "@/lib/sound";

/**
 * Shared mute + volume control for the interactive games. Reads and writes the
 * same localStorage prefs the sound engine uses, so a change here applies to
 * pack tearing, card flips, the Palsino reels and every other cue.
 */
export function SoundControls({ label = "sound", className = "" }: { label?: string; className?: string }) {
  const [{ volume, muted }, setPrefs] = useState({ volume: 0.8, muted: false });

  useEffect(() => setPrefs(loadSfxPrefs()), []);

  const update = (next: { volume: number; muted: boolean }) => {
    unlockAudio();
    setPrefs(next);
    saveSfxPrefs(next);
  };

  return (
    <div className={`flex items-center justify-center gap-2 ${className}`}>
      <button
        type="button"
        onClick={() => update({ volume: volume === 0 ? 0.8 : volume, muted: !muted })}
        aria-label={muted ? `Unmute ${label}` : `Mute ${label}`}
        className="rounded-lg border px-2 py-1 text-sm hover:bg-card"
      >
        <span aria-hidden="true">{muted || volume === 0 ? "🔇" : "🔊"}</span>
      </button>
      <label className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground">
        <span className="sr-only sm:not-sr-only">Volume</span>
        <input
          type="range"
          min={0}
          max={100}
          value={muted ? 0 : Math.round(volume * 100)}
          aria-label={`${label} volume`}
          onChange={(e) => update({ volume: Number(e.target.value) / 100, muted: false })}
          className="w-24 accent-primary"
        />
      </label>
    </div>
  );
}
